/**
 * PolicyVersionHistoryTab -- immutable version history for one policy
 * domain on the tenant policy-engine chassis (overdraft-coverage arc
 * OC-3), plus the examiner-evidence export download.
 *
 * The listing is cursor-paginated newest-first straight off the spec's
 * PolicyVersionPage; "Older versions" advances the cursor and "Newest"
 * resets it. The evidence download fetches the full history with its
 * SHA-256 integrity hash as a JSON attachment.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  DataGrid,
  Skeleton,
  Badge,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import {
  useDownloadPolicyEvidence,
  usePolicyVersions,
  type PolicyDomain,
  type PolicyVersionEntry,
  type TempoDeliveryMode,
} from '../../hooks/useTempoPolicy';
import { deliveryModeBadgeStatus, deliveryModeLabel } from './delivery-mode';
import { BADGE_TONE } from '../collateral/CollateralListParts';

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * One-line dial summary per domain so the grid is scannable without
 * expanding payloads. Unknown/absent fields render as '--'.
 */
function summarizeParameters(entry: PolicyVersionEntry): string {
  const parameters = entry.parameters as Record<string, unknown>;
  if (entry.policyDomain === 'TEMPO') {
    const alpha = parameters.alpha ?? '--';
    const tierCaps = parameters.tierCaps as { entryCap?: number; standardCap?: number } | undefined;
    return `alpha ${alpha}; caps $${tierCaps?.entryCap ?? '--'}/$${tierCaps?.standardCap ?? '--'}`;
  }
  const ratio = parameters.maxPerOriginatorRatio ?? '--';
  const methods = Array.isArray(parameters.allowedSettlementMethods)
    ? (parameters.allowedSettlementMethods as string[]).join('/')
    : '--';
  return `per-originator ${ratio}; settlement ${methods}`;
}

export function PolicyVersionHistoryTab({ domain }: { domain: PolicyDomain }) {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const { data: page, isLoading, error } = usePolicyVersions(domain, cursor);
  const downloadMutation = useDownloadPolicyEvidence();

  const columns = useMemo<ColumnDef<PolicyVersionEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'policyVersion',
        header: 'Version',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            v{getValue() as number}
          </span>
        ),
        size: 90,
      },
      {
        // GH #2744: the retired `enabled` boolean is GONE from the contract, so
        // this column read undefined and stamped every version "Disabled" -- on
        // the tab an examiner reads. A bare accessorKey rename would not have
        // fixed it: all three enum values are truthy, so the boolean ternary
        // below would have rendered OFF as "Enabled", which is worse than the
        // defect it replaced. The label map is the shared one the editor pages
        // use, so the history cannot drift from them again.
        accessorKey: 'deliveryMode',
        header: 'Mode at publish',
        cell: ({ getValue }) => {
          // Typed as required by the spec; a PRE-MIGRATION row carries no stamp
          // at runtime, and that absence renders as an evidence gap.
          const mode = getValue() as TempoDeliveryMode | null | undefined;
          return (
            <Badge variant={BADGE_TONE[deliveryModeBadgeStatus(mode)]} size="small">
              {deliveryModeLabel(mode)}
            </Badge>
          );
        },
        size: 160,
      },
      {
        accessorKey: 'effectiveAt',
        header: 'Effective',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDateTime(getValue() as string | undefined)}
          </span>
        ),
        size: 180,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Published',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDateTime(getValue() as string)}
          </span>
        ),
        size: 180,
      },
      {
        id: 'summary',
        header: 'Dials',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
            {summarizeParameters(row.original)}
          </span>
        ),
        size: 280,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <BodyText variant="ui" className="text-sm text-tertiary">
          Every published version is immutable and remains readable for recommendation audit
          and examiner evidence.
        </BodyText>
        <Button
          variant="secondary"
          onClick={() => downloadMutation.mutate(domain)}
          disabled={downloadMutation.isPending}
        >
          {downloadMutation.isPending ? 'Exporting...' : 'Download evidence export'}
        </Button>
      </div>

      {downloadMutation.isError && (
        <Alert type="error" title="Evidence export failed">
          {downloadMutation.error.status === 404
            ? 'No versions exist for this domain yet -- publish a first version to create the evidence trail.'
            : downloadMutation.error.message}
        </Alert>
      )}

      {error && (
        <Alert type="error" title="Version history unavailable">
          {error.message}
        </Alert>
      )}

      {isLoading ? (
        <Card className="p-6">
          <Skeleton className="h-6 w-full mb-3" />
          <Skeleton className="h-6 w-full mb-3" />
          <Skeleton className="h-6 w-2/3" />
        </Card>
      ) : (page?.items.length ?? 0) === 0 ? (
        <Card className="p-6">
          <BodyText variant="ui" className="text-sm text-tertiary">
            No versions published yet.
          </BodyText>
        </Card>
      ) : (
        <DataGrid<PolicyVersionEntry>
          data={page?.items ?? []}
          columns={columns}
          getRowId={(row) => String(row.policyVersion)}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      <div className="flex items-center gap-2">
        {cursor !== undefined && (
          <Button variant="secondary" onClick={() => setCursor(undefined)}>
            Newest
          </Button>
        )}
        {page?.nextCursor !== undefined && (
          <Button variant="secondary" onClick={() => setCursor(page.nextCursor)}>
            Older versions
          </Button>
        )}
      </div>
    </div>
  );
}
